import React, { useRef } from 'react';

const ACTION_LABELS = {
  application_created: 'Application Created',
  application_submitted: 'Application Submitted',
  verification_started: 'Verification Started',
  clarification_requested: 'Clarification Requested',
  inspection_scheduled: 'Inspection Scheduled',
  inspection_completed: 'Inspection Completed',
  application_approved: 'Application Approved',
  application_rejected: 'Application Rejected',
  payment_received: 'Payment Received',
  license_issued: 'License Issued',
};

const APPLICATION_TYPE_LABELS = {
  new_license: 'New License',
  renewal: 'Renewal',
};

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateTime = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatAmount = (value) => `₹${Number(value || 0).toFixed(2)}`;

const Field = ({ label, value }) => (
  <div className="detail-field">
    <span className="detail-label">{label}</span>
    <span className="detail-value">{value || '—'}</span>
  </div>
);

const PRINT_STYLES = `
  body { font-family: Arial, sans-serif; padding: 24px; color: #222; font-size: 13px; }
  h2 { font-size: 20px; margin: 0 0 2px; }
  h4 { font-size: 14px; margin: 0 0 10px; padding-bottom: 6px; border-bottom: 1px solid #ddd; color: #003d7a; }
  .detail-print-sub { color: #555; font-size: 12px; margin: 0 0 18px; }
  .detail-section { margin-bottom: 18px; page-break-inside: avoid; }
  .detail-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px 24px; }
  .detail-field { display: flex; flex-direction: column; }
  .detail-label { font-size: 11px; color: #777; text-transform: uppercase; letter-spacing: 0.03em; }
  .detail-value { font-size: 13px; font-weight: 600; }
  .status-badge { display: inline-block; padding: 2px 10px; border-radius: 12px; background: #e8f4fd; color: #0066cc; font-size: 12px; text-transform: capitalize; font-weight: 600; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
  th { background: #f5f8fb; }
  .status-timeline { list-style: none; margin: 0; padding: 0; }
  .status-timeline-item { display: flex; gap: 10px; padding-bottom: 12px; }
  .timeline-dot { width: 10px; height: 10px; border-radius: 50%; background: #0066cc; margin-top: 4px; flex-shrink: 0; }
  .timeline-content { display: flex; flex-direction: column; }
  .timeline-content strong { font-size: 13px; }
  .timeline-date, .timeline-by { font-size: 11px; color: #777; }
  .timeline-remarks { margin: 2px 0 0; font-size: 12px; color: #555; font-style: italic; }
  .no-print { display: none !important; }
`;

const ApplicationDetailView = ({ application }) => {
  const printRef = useRef(null);
  const app = application;
  const history = app.approval_history || [];
  const documents = app.documents || [];

  const applicantName = [app.applicant_first_name_en, app.applicant_last_name_en]
    .filter(Boolean).join(' ') || app.applicant_name;

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank', 'width=900,height=1000');
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${app.application_number} - Application Details</title>
          <style>${PRINT_STYLES}</style>
        </head>
        <body>${content.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <div className="application-detail-view">
      <div className="detail-view-toolbar no-print">
        <button type="button" className="btn btn-secondary btn-sm" onClick={handlePrint}>
          🖨️ Print Details
        </button>
      </div>

      <div ref={printRef}>
        <div className="detail-print-header">
          <h2>{app.business_name_en}</h2>
          <p className="detail-print-sub">
            Trade License Application — {app.application_number}
          </p>
        </div>

        <div className="detail-section">
          <h4>Application Summary</h4>
          <div className="detail-grid">
            <Field label="Application Number" value={app.application_number} />
            <Field
              label="Status"
              value={<span className={`status-badge ${app.status}`}>{app.status}</span>}
            />
            <Field label="Application Type" value={APPLICATION_TYPE_LABELS[app.application_type] || app.application_type} />
            <Field label="Current Stage" value={app.current_stage_name} />
            <Field label="Trade Category" value={app.trade_category_name} />
            <Field label="Submitted On" value={formatDateTime(app.submitted_date)} />
            {app.application_type === 'renewal' && (
              <Field label="Existing License #" value={app.existing_license_number} />
            )}
            {app.license_number && (
              <Field label="License Number" value={app.license_number} />
            )}
          </div>
        </div>

        <div className="detail-section">
          <h4>Applicant Details</h4>
          <div className="detail-grid">
            <Field label="Applicant Name" value={applicantName} />
            {app.applicant_name_hi && <Field label="Name (Hindi)" value={app.applicant_name_hi} />}
            <Field label="Father / Husband Name" value={app.applicant_father_husband_name_en} />
            <Field label="Gender" value={app.applicant_gender} />
            <Field label="Mobile" value={app.applicant_phone} />
            <Field label="Email" value={app.applicant_email} />
            <Field label="Aadhaar" value={app.applicant_aadhar} />
            <Field label="PAN" value={app.applicant_pan} />
          </div>
        </div>

        <div className="detail-section">
          <h4>Business Details</h4>
          <div className="detail-grid">
            <Field label="Business Name" value={app.business_name_en} />
            {app.business_name_hi && <Field label="Business Name (Hindi)" value={app.business_name_hi} />}
            <Field label="Business Type" value={app.business_type} />
            <Field label="Ownership Type" value={app.ownership_type} />
            <Field label="GST Number" value={app.gst_number} />
          </div>
        </div>

        <div className="detail-section">
          <h4>Location & Property</h4>
          <div className="detail-grid">
            <Field label="Ward Number" value={app.ward_number} />
            <Field label="Holding Number" value={app.holding_number} />
            <Field label="Business Address" value={app.address_en} />
            <Field label="Property Owner" value={app.property_owner_name_en} />
            <Field label="Property Owner Address" value={app.property_owner_address_en} />
          </div>
        </div>

        <div className="detail-section">
          <h4>Fees & Payment</h4>
          <div className="detail-grid">
            <Field label="Application Fee" value={formatAmount(app.application_fee)} />
            <Field label="Amount Paid" value={formatAmount(app.paid_amount)} />
            {Number(app.pending_payment_amount || 0) > 0 && (
              <Field label="Payment Under Verification" value={formatAmount(app.pending_payment_amount)} />
            )}
            <Field
              label="Balance Due"
              value={formatAmount(
                Math.max(0, Number(app.application_fee || 0) - Number(app.paid_amount || 0))
              )}
            />
          </div>
        </div>

        {documents.length > 0 && (
          <div className="detail-section">
            <h4>Documents Submitted</h4>
            <table className="detail-doc-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Document</th>
                  <th>Type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc, idx) => (
                  <tr key={doc.id}>
                    <td>{idx + 1}</td>
                    <td>{doc.name_en}</td>
                    <td>{doc.document_type?.replace(/_/g, ' ')}</td>
                    <td style={{ textTransform: 'capitalize' }}>{doc.verification_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="detail-section">
          <h4>Progress Timeline</h4>
          {history.length === 0 ? (
            <p className="empty-msg">No progress updates recorded yet.</p>
          ) : (
            <ol className="status-timeline">
              {[...history].reverse().map((item) => (
                <li key={item.id} className="status-timeline-item">
                  <div className="timeline-dot" />
                  <div className="timeline-content">
                    <strong>{ACTION_LABELS[item.action] || item.action.replace(/_/g, ' ')}</strong>
                    <span className="timeline-date">{formatDateTime(item.performed_date)}</span>
                    {item.performed_by_name && (
                      <span className="timeline-by">by {item.performed_by_name}</span>
                    )}
                    {item.remarks && <p className="timeline-remarks">{item.remarks}</p>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        <p className="detail-print-footer">
          Generated on {formatDate(new Date())} — This is a system-generated status report.
        </p>
      </div>
    </div>
  );
};

export default ApplicationDetailView;
